import { axiosInstance } from '@/lib/utils';
import { endpoints } from '@/api';
import type {
  AnalyticsSales,
  AnalyticsTransactions,
  AnalyticsProducts,
  AnalyticsCustomers,
  RequestResponse,
  DashboardAnalytics,
  RecentChat,
  RecentChatChannel,
} from '@/types';

type UnknownChartPoint = Record<string, unknown>;

export const normalizeChart = (
  chart: unknown,
  valueKeys: string[]
): Array<{ month: string; sales: number }> => {
  if (!Array.isArray(chart)) return [];
  return (chart as UnknownChartPoint[])
    .map((p) => {
      const month =
        (p.period as string) ||
        (p.month as string) ||
        (p.label as string) ||
        (p.date as string) ||
        '';
      const value =
        valueKeys
          .map((k) => p[k])
          .find((v) => typeof v === 'number' || typeof v === 'string') ?? 0;
      const num =
        typeof value === 'number'
          ? value
          : parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
      return { month, sales: num };
    })
    .filter((p) => !!p.month);
};

export const fillMissingMonths = (
  chart: Array<{ month: string; sales: number }>
): Array<{ month: string; sales: number }> => {
  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => {
    const month = String(i + 1).padStart(2, '0');
    return `${currentYear}-${month}`;
  });

  const chartMap = new Map(chart.map((p) => [p.month, p.sales]));

  return months.map((m) => ({
    month: m,
    sales: chartMap.get(m) ?? 0,
  }));
};

export const fetchAnalyticsSales = async (
  startDate?: string,
  endDate?: string
) => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await axiosInstance<RequestResponse<AnalyticsSales>>(
    endpoints.analytics.sales,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  const data = response.data.data;
  if (!data) {
    return {
      totalSales: 0,
      dateRange: {},
      chart: fillMissingMonths([]),
    };
  }

  const normalizedChart = normalizeChart(data.chart, [
    'total',
    'sales',
    'totalSales',
    'amount',
    'value',
  ]);

  return {
    totalSales: data.totalSales ?? 0,
    dateRange: data.dateRange ?? {},
    chart: fillMissingMonths(normalizedChart),
  };
};

export const fetchAnalyticsTransactions = async (
  startDate?: string,
  endDate?: string
) => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await axiosInstance<RequestResponse<AnalyticsTransactions>>(
    endpoints.analytics.transactions,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  const data = response.data.data;
  if (!data) {
    return {
      totalPayouts: 0,
      dateRange: {},
      chart: fillMissingMonths([]),
    };
  }

  const normalizedChart = normalizeChart(data.chart, [
    'payouts',
    'totalPayouts',
    'amount',
    'value',
  ]);

  return {
    totalPayouts: data.totalPayouts ?? 0,
    dateRange: data.dateRange ?? {},
    chart: fillMissingMonths(normalizedChart),
  };
};

export const fetchAnalyticsProducts = async (
  startDate?: string,
  endDate?: string
) => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await axiosInstance<RequestResponse<AnalyticsProducts>>(
    endpoints.analytics.products,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  const data = response.data.data;
  if (!data) {
    return {
      dateRange: {},
      chart: fillMissingMonths([]),
    };
  }

  const normalizedChart = normalizeChart(data.chart, [
    'products',
    'count',
    'value',
  ]);

  return {
    dateRange: data.dateRange ?? {},
    chart: fillMissingMonths(normalizedChart),
    mostOrderedProduct: data.mostOrderedProduct,
    topRevenueProduct: data.topRevenueProduct,
  };
};

export const fetchAnalyticsCustomers = async ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
} = {}) => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await axiosInstance<RequestResponse<AnalyticsCustomers>>(
    endpoints.analytics.customers,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  const data = response.data.data;
  // Ensure barchart is always an array
  return data
    ? {
        ...data,
        barchart: Array.isArray(data.barchart) ? data.barchart : [],
      }
    : {
        totalCustomers: 0,
        totalLeads: 0,
        barchart: [],
        newCustomers: 0,
        returningCustomers: 0,
        conversionRate: 0,
      };
};

export const fetchDashboardAnalytics = async ({
  startDate,
  endDate,
}: {
  startDate?: string;
  endDate?: string;
}) => {
  const params: Record<string, string> = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;

  const response = await axiosInstance<RequestResponse<DashboardAnalytics>>(
    endpoints.analytics.dashboard,
    { params: Object.keys(params).length > 0 ? params : undefined }
  );
  return response.data.data;
};

export const fetchRecentChats = async (
  channel: RecentChatChannel
): Promise<RecentChat[]> => {
  const response = await axiosInstance.get<RequestResponse<RecentChat[]>>(
    endpoints.analytics.recentChats,
    { params: { channel } }
  );
  const data = response.data.data;
  return Array.isArray(data) ? data : [];
};
